import React, { useEffect, useRef, useState } from "react";
import { sendGtmEvent } from "~/analytics/definitions";
import { bindRecAiClickEvent, getCarouselObserver } from "~/userAnalytics";
import { CarouselItem } from "../Carousel/CarouselItem";
import CarouselMember from "../Carousel/CarouselMember";
import { ProductDetailPageResult } from "../ProductDetailPageResult";
import "../productCarousel.scss";
import RecommendationCarouselPopover from "./RecommendationCarouselPopover";
import getABFeatureFlag from "~/common/Featbit/getABFeatureFlag";
import { User } from "~/common/Store/models/User";
import connectUserAndChat from "~/common/Store/connectUserAndChat";
import { getRecommendationCarouselItems } from "./getRecommendationCarouselItems";

interface Props {
  product: ProductDetailPageResult;
  mounted: boolean;
  user: User;
}

const RecommendationCarousel = connectUserAndChat(
  ({ mounted, user, product }: Props) => {
    const [recommendationCarouselItems, setRecommendationCarouselItems] =
      useState<CarouselItem[]>([]);

    const [activeSaleClassNames, setActiveSaleClassNames] =
      useState<string>("");
    const [eventName, setEventName] = useState<string>("recAiEvent");
    const [eventLabel, setEventLabel] = useState<string>(
      "Two_Tower_PDP_V1.0.0",
    );
    const [abVariation, setAbVariation] = useState<string>("A");
    const isRecommendationCarouselComponentMounted = useRef(false);

    useEffect(() => {
      isRecommendationCarouselComponentMounted.current = true;
      const fetchABVariationData = async (
        sessionId: string,
        emailAddress?: string,
      ) => {
        const result = await getABFeatureFlag(
          {
            featureFlagName: "pdp-iyml-carousel-ab",
            defaultValue: "A",
            customProps: [],
          },
          { sessionId, emailAddress },
        );

        if (result.isError) {
          return { modelName: "pdp_recommendations", featureFlagValue: "A" };
        }

        const variationValue = result.value.stringValue;
        // Next few lines of code is to parse the model name from Featbit variationlabel(Format: <modelName.variationid>) added specifically for A/A test
        // Note: ModelName is passed to AIPE call and "." is invalid character in AIPE model request.
        const parsedVariationValues = variationValue.split(".");
        const modelName = parsedVariationValues[0] ?? "pdp_recommendations";
        const featureFlagValue = parsedVariationValues[1] ?? "A";

        return { modelName, featureFlagValue };
      };

      const fetchRecommendationsBasedOnABVariation = async (
        sessionId: string,
        emailAddress?: string,
      ) => {
        const { modelName, featureFlagValue } = await fetchABVariationData(
          sessionId,
          emailAddress,
        );
        setAbVariation(featureFlagValue);

        if (mounted) {
          getRecommendationCarouselItems({
            activeTemplate: product,
            modelName,
            abVariation: featureFlagValue,
            isMounted: () => isRecommendationCarouselComponentMounted.current,
            setEventName,
            setEventLabel,
            setRecommendationCarouselItems,
          });
        }
      };

      if (user.sessionId) {
        fetchRecommendationsBasedOnABVariation(
          user.sessionId,
          user.emailAddress,
        );
      }

      return () => {
        isRecommendationCarouselComponentMounted.current = false;
      };
    }, [mounted, product, user.sessionId, user.emailAddress]);

    useEffect(() => {
      async function importCarouselFunctionality() {
        if (document.querySelector("[data-component=carousel]")) {
          const { default: initCategoryCarousels } = await import(
            "~/common/initCategoryCarousels"
          );
          initCategoryCarousels();
        }
      }

      if (recommendationCarouselItems?.length) {
        const carouselAnchor = document.querySelector(
          "#recommendationCarousel",
        );

        if (carouselAnchor instanceof HTMLElement) {
          const carouselObserver = getCarouselObserver(
            carouselAnchor,
            "recAiEvent",
          );
          carouselObserver.observe(carouselAnchor);
        }

        sendGtmEvent({
          event: eventName,
          eventCategory: "Items You May Like",
          eventLabel: `${eventLabel}.${abVariation}`,
          eventAction: "AI Impression",
        });

        importCarouselFunctionality();

        bindRecAiClickEvent("#recommendationCarousel");
        setActiveSaleClassNames(
          recommendationCarouselItems?.some((x) => x.displaySalePrice)
            ? "h-[37px]"
            : "h-[22px]",
        );
      }
    }, [recommendationCarouselItems, eventName, eventLabel, abVariation]);

    return (
      <>
        {!!recommendationCarouselItems?.length && (
          <div
            id="recommendationCarousel"
            data-base-itemnumber={product.itemNumber}
            data-placement-id={`${eventLabel}.${abVariation}`}
            data-category="Items You May Like"
            className="order-[-2] lt:order-none float-left w-full"
          >
            <div className="clearfix bg-white border-gray-300 border-solid border-0 border-t p-3 xs:bg-transparent xs:border-none xs:border-t-0 xs:p-0">
              <div className="carousel-wrap__header">
                <h2 className="pull-left flex items-center mb-0 xs:mb-4 text-lg leading-5">
                  Items You Might Like
                  <RecommendationCarouselPopover />
                </h2>
              </div>
            </div>
            <div
              data-component="products-suggested-for-you-carousel"
              className="carousel-wrap"
              data-testid="products-suggested-for-you-carousel"
              gtm-carousel-name="Items You Might Like"
              gtm-carousel-model={`${eventLabel}.${abVariation}`}
            >
              <div
                id="other-products"
                className="carousel-box"
                data-component="carousel"
              >
                <div className="ag-carousel-nav">
                  <button
                    type="button"
                    className="ag-car-prev"
                    data-control="prev"
                  >
                    <span className="sr-only">
                      show previous carousel items
                    </span>
                  </button>
                </div>
                <div
                  id="otherProductsCarousel"
                  className="ag-carousel-frame box"
                >
                  <ul className="ag-carousel ag-mini" data-slide-width="220">
                    {recommendationCarouselItems.map(
                      (carouselItem, index) =>
                        carouselItem && (
                          <CarouselMember
                            item={carouselItem}
                            showAddToCart
                            addToCartFormId={`relatedproduct_companion-items-carousel_${index}`}
                            key={index}
                            activeSalePricingClassNames={activeSaleClassNames}
                            isMiniCarousel
                          />
                        ),
                    )}
                  </ul>
                </div>
                <div className="ag-carousel-nav">
                  <button
                    type="button"
                    className="ag-car-next"
                    data-control="next"
                  >
                    <span className="sr-only">show more carousel items</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  },
);

export default RecommendationCarousel;
